import React from "react";
import { observer } from "mobx-react";
import { Button, Divider, Popover, Switch, Tooltip } from "antd";
import { translateKey as t } from "../../../locales/translation";
import {
  faChartColumn,
  faEye,
  faEyeSlash,
  faFloppyDisk,
  faIdCard,
  faPlus,
  faRotateLeft,
  faTable,
  faTrash,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface CrudTableButtonsProps {
  controller?: any;
  onDelete?: any;
  overrideOnCreate?: boolean;
  onCreate?: any;
  onBulkUpdate?: any;
  singleDeleteOnly?: boolean;
  createButtonTitle?: string;
  hideDeleteButton?: boolean;
  hideDiscardButton?: boolean;
  hideSubmitButton?: boolean;
  hideDivider?: boolean;
}

const CrudTableButtons: React.FC<CrudTableButtonsProps> = observer(
  ({
    controller,
    onDelete,
    onCreate,
    overrideOnCreate,
    onBulkUpdate,
    singleDeleteOnly,
    createButtonTitle,
    hideDeleteButton,
    hideDiscardButton,
    hideSubmitButton,
    hideDivider,
  }) => {
    const handleCreateButtonClick = () => {
      if (!overrideOnCreate) {
        // once create button is clicked section is now CREATE
        controller.setFormMode("create");
        controller.tableInstance.handleCellEdit();
        // display form
        controller.setIsFormModalVisible(true);
      }

      // run onCreate function
      onCreate();
    };

    const renderButtons = () => {
      const {
        mainDataHasLoaded,
        isReadOnly,
        tableIsReadOnly,
        modifiedRows,
        handleTableSubmit,
        tableInstance,
        deleteModal,
        saveModal,
        showDepotSelector,
        additionalColumnsAreVisible,
        analyticsModalIsVisible,
      } = controller;

      const addNewButton = () => {
        const isDisabled = showDepotSelector || false;
        // const isDisabled = (showDepotSelector && !this.depotSelected) || false

        if (isReadOnly) {
          return (
            <Tooltip
              placement="bottom"
              title={t(
                "lblThisPageIsReadOnlyYouDoNotHavePermissionToCreateNewRecords",
                "This page is set as read-only: you do not have permission to create new records"
              )}
            >
              <Button
                data-e2e="table-buttons-add-btn"
                type="primary"
                disabled={true}
                icon={<FontAwesomeIcon icon={faPlus} />}
                size="small"
                style={{ marginRight: 10 }}
              >
                {!tableInstance.mobileRender ? (
                  <>
                    {t("lblAdd", "Add")} {createButtonTitle || t("lblItem", "Item")}
                  </>
                ) : null}
              </Button>
            </Tooltip>
          );
        } else {
          if (!tableInstance.mobileRender) {
            return (
              <Popover
                content={
                  <div>
                    <strong>{t("lblAddAltPlusN", "Add (ALT + N)")}</strong>
                    <br />
                    <span>{t("lblOpenTheCreateForm", "Open the create form")}</span>
                  </div>
                }
                placement="bottom"
                mouseEnterDelay={0.75}
                mouseLeaveDelay={0.5}
              >
                <Button
                  onClick={handleCreateButtonClick}
                  data-e2e="table-buttons-add-btn"
                  type="primary"
                  disabled={isDisabled || (mainDataHasLoaded !== undefined && !mainDataHasLoaded)}
                  icon={<FontAwesomeIcon icon={faPlus} />}
                  size="small"
                  style={hideDeleteButton ? { marginRight: 0 } : { marginRight: 10 }}
                >
                  {t("lblAdd", "Add") + " " + createButtonTitle || t("lblItem", "Item")}
                </Button>
              </Popover>
            );
          } else {
            return (
              <Button
                onClick={handleCreateButtonClick}
                data-e2e="table-buttons-add-btn"
                type="primary"
                disabled={isDisabled || (mainDataHasLoaded !== undefined && !mainDataHasLoaded)}
                icon={<FontAwesomeIcon icon={faPlus} />}
                size="small"
                style={hideDeleteButton ? { marginRight: 0 } : { marginRight: 10 }}
              />
            );
          }
        }
      };

      const deleteButton = () => {
        // Used when multiple-delete is allowed
        let deleteCount: number = 0;
        // Used when only single delete is allowed
        let isEnabled: boolean = false;

        if (singleDeleteOnly) {
          if (!isReadOnly && tableInstance?.selectedRow.row) {
            isEnabled = true;
          }
        } else {
          if (tableInstance?.multiSelectedRows.totSelected > 0) {
            deleteCount = tableInstance.multiSelectedRows.totSelected;
          }
        }

        if (isReadOnly) {
          return (
            <Tooltip
              placement="bottom"
              title={t(
                "lblThisPageIsReadOnlyYouDoNotHavePermissionToDeleteRecords",
                "This page is set as read-only: you do not have permission to delete records"
              )}
            >
              <Button
                data-e2e="table-buttons-delete-btn"
                danger
                disabled={true}
                icon={<FontAwesomeIcon icon={faTrash} />}
                size="small"
                style={tableIsReadOnly ? { marginRight: 0 } : { marginRight: 10 }}
              >
                {!tableInstance.mobileRender ? t("lblDelete", "Delete") : null}
              </Button>
            </Tooltip>
          );
        } else {
          if (!tableInstance.mobileRender) {
            return (
              <Popover
                content={
                  <div>
                    <strong>{t("lblDeleteAltPlusR", "Delete (ALT + R)")}</strong>
                    <br />
                    <span>{t("lblDeleteSelectedRows", "Delete selected rows")}</span>
                  </div>
                }
                placement="bottom"
                mouseEnterDelay={0.75}
                mouseLeaveDelay={0.5}
              >
                {singleDeleteOnly ? (
                  <Button
                    onClick={() => {
                      deleteModal.onConfirmDelete = () => onDelete();
                      deleteModal.description = t(
                        "lblAreYouSureYouWantToDeleteOneItem",
                        "Are you sure you want to delete 1 item?"
                      );
                      deleteModal.visible = true;
                    }}
                    data-e2e="table-buttons-delete-btn"
                    danger
                    disabled={!isEnabled}
                    icon={<FontAwesomeIcon icon={faTrash} />}
                    size="small"
                    style={tableIsReadOnly ? { marginRight: 0 } : { marginRight: 10 }}
                  >
                    {!tableInstance.mobileRender ? t("lblDelete", "Delete") : null}
                  </Button>
                ) : (
                  <Button
                    onClick={() => {
                      deleteModal.onConfirmDelete = () => onDelete();
                      deleteModal.description =
                        tableInstance.multiSelectedRows.totSelected === 1
                          ? t("lblAreYouSureYouWantToDeleteOneItem", "Are you sure you want to delete 1 item?")
                          : t("lblAreYouSureYouWantToDeleteCountItems", "Are you sure you want to delete {1} items?", [
                              tableInstance.multiSelectedRows.totSelected,
                            ]);
                      deleteModal.visible = true;
                    }}
                    data-e2e="table-buttons-delete-btn"
                    danger
                    disabled={deleteCount === 0}
                    icon={<FontAwesomeIcon icon={faTrash} />}
                    size="small"
                    style={tableIsReadOnly ? { marginRight: 0 } : { marginRight: 10 }}
                  >
                    {!tableInstance.mobileRender ? (
                      deleteCount === 0 ? (
                        <>{t("lblDelete", "Delete")}</>
                      ) : (
                        <>
                          {t("lblDelete", "Delete")} ({deleteCount})
                        </>
                      )
                    ) : null}
                  </Button>
                )}
              </Popover>
            );
          } else {
            return (
              <>
                {singleDeleteOnly ? (
                  <Button
                    onClick={() => {
                      deleteModal.onConfirmDelete = () => onDelete();
                      deleteModal.description = t(
                        "lblAreYouSureYouWantToDeleteOneItem",
                        "Are you sure you want to delete 1 item?"
                      );
                      deleteModal.visible = true;
                    }}
                    data-e2e="table-buttons-delete-btn"
                    danger
                    disabled={!isEnabled}
                    icon={<FontAwesomeIcon icon={faTrash} />}
                    size="small"
                    style={tableIsReadOnly ? { marginRight: 0 } : { marginRight: 10 }}
                  />
                ) : (
                  <Button
                    onClick={() => {
                      deleteModal.onConfirmDelete = () => onDelete();
                      deleteModal.description =
                        tableInstance.multiSelectedRows.totSelected === 1
                          ? t("lblAreYouSureYouWantToDeleteOneItem", "Are you sure you want to delete 1 item?")
                          : t("lblAreYouSureYouWantToDeleteCountItems", "Are you sure you want to delete {1} items?", [
                              tableInstance.multiSelectedRows.totSelected,
                            ]);
                      deleteModal.visible = true;
                    }}
                    data-e2e="table-buttons-delete-btn"
                    danger
                    disabled={deleteCount === 0}
                    icon={<FontAwesomeIcon icon={faTrash} />}
                    size="small"
                    style={tableIsReadOnly ? { marginRight: 0 } : { marginRight: 10 }}
                  />
                )}
              </>
            );
          }
        }
      };

      const discardButton = () => {
        let discardCount: number = 0;

        if (modifiedRows.length > 0) {
          // deleteCount = selectedRows.length
          discardCount = modifiedRows.length;
        }

        let style = {
          marginRight: 10,
          backgroundColor: modifiedRows.length === 0 ? null : "#faad14",
          borderColor: modifiedRows.length === 0 ? null : "#faad14",
        };

        if (isReadOnly) {
          return (
            <Tooltip
              placement="bottom"
              title={t(
                "lblThisPageIsReadOnlyYouDoNotHavePermissionToModifyRecords",
                "This page is set as read-only: you do not have permission to modify records"
              )}
            >
              <Button
                data-e2e="table-buttons-discard-btn"
                type="primary"
                disabled={true}
                icon={<FontAwesomeIcon icon={faRotateLeft} />}
                size="small"
                style={style}
              >
                {!tableInstance.mobileRender ? t("lblDiscardChanges", "Discard Changes") : null}
              </Button>
            </Tooltip>
          );
        } else {
          if (!tableInstance.mobileRender) {
            return (
              <Popover
                content={
                  <div>
                    <strong>{t("lblDiscardChangesAltPlusB", "Discard changes (ALT + B)")}</strong>
                    <br />
                    <span>{t("lblDiscardUnsavedTableChanges", "Discard unsaved table changes")}</span>
                  </div>
                }
                placement="bottom"
                mouseEnterDelay={0.75}
                mouseLeaveDelay={0.5}
              >
                <Button
                  onClick={() => {
                    controller.tableInstance.handleCellEdit();
                    controller.isDiscardChangesModalVisible = true;
                  }}
                  data-e2e="table-buttons-discard-btn"
                  type="primary"
                  disabled={modifiedRows.length === 0}
                  icon={<FontAwesomeIcon icon={faRotateLeft} />}
                  size="small"
                  style={style}
                >
                  {!tableInstance.mobileRender ? (
                    discardCount === 0 ? (
                      <>{t("lblDiscardChanges", "Discard Changes")}</>
                    ) : (
                      <>
                        {t("lblDiscardChanges", "Discard Changes")} ({discardCount})
                      </>
                    )
                  ) : null}
                </Button>
              </Popover>
            );
          } else {
            return (
              <Button
                onClick={() => {
                  controller.tableInstance.handleCellEdit();
                  controller.isDiscardChangesModalVisible = true;
                }}
                data-e2e="table-buttons-discard-btn"
                type="primary"
                disabled={modifiedRows.length === 0}
                icon={<FontAwesomeIcon icon={faRotateLeft} />}
                size="small"
                style={style}
              />
            );
          }
        }
      };

      const submitButton = () => {
        let saveCount: number = 0;

        if (modifiedRows.length > 0) {
          // deleteCount = selectedRows.length
          saveCount = modifiedRows.length;
        }

        if (isReadOnly) {
          return (
            <Tooltip
              placement="bottom"
              title={t(
                "lblThisPageIsReadOnlyYouDoNotHavePermissionToModifyRecords",
                "This page is set as read-only: you do not have permission to modify records"
              )}
            >
              <Button
                data-e2e="table-buttons-save-btn"
                type="primary"
                disabled={true}
                icon={<FontAwesomeIcon icon={faFloppyDisk} />}
                size="small"
              >
                {t("lblSave", "Save")}
              </Button>
            </Tooltip>
          );
        } else {
          return (
            <Popover
              content={
                <div>
                  <strong>{t("lblSaveTableAltPlusEnter", "Save table (ALT + ENTER)")}</strong>
                  <br />
                  <span>{t("lblBulkUpdateTableChanges", "Bulk update table changes")}</span>
                </div>
              }
              placement="bottom"
              mouseEnterDelay={0.75}
              mouseLeaveDelay={0.5}
            >
              <Button
                onClick={() => {
                  controller.tableInstance.handleCellEdit();
                  saveModal.onConfirmSave = () => handleTableSubmit();
                  saveModal.description = t("lblConfirmChanges", "Confirm Changes");
                  saveModal.visible = true;
                }}
                data-e2e="table-buttons-save-btn"
                type="primary"
                disabled={modifiedRows.length === 0}
                icon={<FontAwesomeIcon icon={faFloppyDisk} />}
                size="small"
              >
                {t("lblSave", "Save")} {saveCount !== 0 && `(${saveCount})`}
              </Button>
            </Popover>
          );
        }
      };

      const analyticsButton = () => {
        if (!tableInstance.mobileRender) {
          return (
            <Popover
              content={
                <div>
                  <strong>{t("lblAnalyticsAltPlusG", "Analytics (ALT + G)")}</strong>
                  <br />
                  <span>{t("lblOpenDataAnalysisSection", "Open data analysis section")}</span>
                </div>
              }
              placement="bottom"
              mouseEnterDelay={0.75}
              mouseLeaveDelay={0.5}
            >
              <Button
                onClick={() => (controller.analyticsModalIsVisible = true)}
                data-e2e="analytics-section-btn"
                disabled={controller.tableInstance?.sortedFilteredList.length === 0}
                icon={<FontAwesomeIcon icon={faChartColumn} />}
                size="small"
                style={{ marginRight: 10 }}
              >
                {!tableInstance.mobileRender ? t("lblAnalytics", "Analytics") : null}
              </Button>
            </Popover>
          );
        } else {
          return (
            <Button
              onClick={() => (controller.analyticsModalIsVisible = true)}
              data-e2e="analytics-section-btn"
              disabled={controller.tableInstance?.sortedFilteredList.length === 0}
              icon={<FontAwesomeIcon icon={faChartColumn} />}
              size="small"
              style={{ marginRight: 10 }}
            />
          );
        }
      };

      const additionalColumnsButton = () => {
        if (!tableInstance.mobileRender) {
          return (
            <Popover
              content={
                <div>
                  <strong>
                    {additionalColumnsAreVisible
                      ? t("lblHideAdditionalColumnsAltPlusLess", 'Hide Additional Columns (ALT + "-")')
                      : t("lblShowAdditionalColumnsAltPlusPlus", 'Show Additional Columns (ALT + "+")')}
                  </strong>
                  <br />
                  <span>{t("lblIndexCreatedAtUpdatedAt", "Index - Created At - Updated At")}</span>
                </div>
              }
              placement="bottom"
              mouseEnterDelay={0.75}
              mouseLeaveDelay={0.5}
            >
              <Switch
                onClick={() => controller.setAdditionalColumnsAreVisible(!controller.additionalColumnsAreVisible)}
                checked={controller.additionalColumnsAreVisible}
                checkedChildren={<FontAwesomeIcon icon={faEye} />}
                unCheckedChildren={<FontAwesomeIcon icon={faEyeSlash} />}
                style={{ marginTop: 1, marginRight: 10 }}
                data-e2e="table-buttons-additional-fields-switch"
              />
            </Popover>
          );
        } else {
          return (
            <Switch
              onClick={() => controller.setAdditionalColumnsAreVisible(!controller.additionalColumnsAreVisible)}
              checked={controller.additionalColumnsAreVisible}
              checkedChildren={<FontAwesomeIcon icon={faEye} />}
              unCheckedChildren={<FontAwesomeIcon icon={faEyeSlash} />}
              style={{ marginTop: 1, marginRight: 10 }}
              data-e2e="table-buttons-additional-fields-switch"
            />
          );
        }
      };

      const resetRowsSelectionButton = () => {
        const { tableInstance } = controller;

        let isRowSelected: boolean = tableInstance.multiSelectedRows.totSelected > 0 || tableInstance.selectedRow.row;

        if (!tableInstance.mobileRender) {
          return (
            <Popover
              content={
                <div>
                  <strong>{t("lblDeselectRowsEsc", "Deselect Rows (ESC)")}</strong>
                  <br />
                  <span>{t("lblResetRowsSelection", "Reset Rows Selection")}</span>
                </div>
              }
              placement="bottom"
              mouseEnterDelay={0.75}
              mouseLeaveDelay={0.5}
            >
              <Button
                onClick={() => {
                  tableInstance.resetMultiselectedRows();
                  tableInstance.resetSelectedRow();
                }}
                type="primary"
                disabled={!isRowSelected}
                icon={<FontAwesomeIcon icon={faXmark} />}
                size="small"
                style={hideDivider ? { marginRight: 5 } : { marginRight: 0 }}
              />
            </Popover>
          );
        } else {
          return (
            <Button
              onClick={() => {
                tableInstance.resetMultiselectedRows();
                tableInstance.resetSelectedRow();
              }}
              type="primary"
              disabled={!isRowSelected}
              icon={<FontAwesomeIcon icon={faXmark} />}
              size="small"
              style={hideDivider ? { marginRight: 5 } : { marginRight: 0 }}
            />
          );
        }
      };

      const cardTableSwitch = () => {
        if (!tableInstance.mobileRender) {
          return (
            <Popover
              content={
                <div>
                  <strong>
                    {tableInstance.columnCardFlag
                      ? t("lblSwitchToTableView", "Switch To Table View")
                      : t("lblSwitchToCardView", "Switch To Card View")}
                  </strong>
                  <br />
                </div>
              }
              placement="bottom"
              mouseEnterDelay={0.75}
              mouseLeaveDelay={0.5}
            >
              <Switch
                onClick={() => tableInstance.handleTableCardChange()}
                checked={tableInstance.columnCardFlag}
                checkedChildren={<FontAwesomeIcon icon={faIdCard} />}
                unCheckedChildren={<FontAwesomeIcon icon={faTable} />}
                style={{ marginTop: 1, marginRight: 10 }}
                data-e2e="table-buttons-additional-fields-switch"
              />
            </Popover>
          );
        } else {
          return (
            <Switch
              onClick={() => tableInstance.handleTableCardChange()}
              checked={tableInstance.columnCardFlag}
              checkedChildren={<FontAwesomeIcon icon={faIdCard} />}
              unCheckedChildren={<FontAwesomeIcon icon={faTable} />}
              style={{ marginTop: 1, marginRight: 10 }}
              data-e2e="table-buttons-additional-fields-switch"
            />
          );
        }
      };

      return (
        <div style={{ display: "flex" }}>
          <div style={{ flex: 1 }}>{resetRowsSelectionButton()}</div>

          {!hideDivider && <Divider type="vertical" style={{ height: "100%" }} />}

          <div style={{ flex: 1 }}>{addNewButton()}</div>
          {/* <div style={{ flex: 1 }}>{bulkUpdateButton()}</div> */}
          {!hideDeleteButton && <div style={{ flex: 1 }}>{deleteButton()}</div>}
          {!tableIsReadOnly && !hideDiscardButton && <div style={{ flex: 1 }}>{discardButton()}</div>}
          {!tableIsReadOnly && !hideSubmitButton && <div style={{ flex: 1 }}>{submitButton()}</div>}

          {!hideDivider && <Divider type="vertical" style={{ height: "100%" }} />}
          {analyticsModalIsVisible !== undefined && <div style={{ flex: 1 }}>{analyticsButton()}</div>}
          {tableInstance.mobileRender && <div style={{ flex: 1 }}>{cardTableSwitch()}</div>}
          {additionalColumnsAreVisible !== undefined && <div style={{ flex: 1 }}>{additionalColumnsButton()}</div>}
        </div>
      );
    };

    return renderButtons();
  }
);
export { CrudTableButtons };
